import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import useIsCrossChain from "./useIsCrossChain";
import { useAccount, useNetwork, useSwitchNetwork } from "wagmi";
import {
  Chain,
  createPublicClient,
  createWalletClient,
  custom,
  http,
  maxUint256,
  numberToHex,
  parseUnits,
  toHex,
} from "viem";
import inscriptionABI from "data/abi";
import {
  DEF_TOKEN_ADDRESS,
  formatSlippageToPerTenThousand,
} from "utils";
import { setWalletWinOpened } from "./redux/slices/walletSlice";
import {
  ApiSubmitOrderParams,
  getAggregateSwap,
} from "services/commonServices";
import { setOpenSlippageWin, setRefreshData } from "./redux/slices/appSlice";
import { setResetInfo } from "./redux/slices/coreInfoSlice";
import toast from "react-hot-toast";
import { setSwapLoading, setSwapStatus } from "./redux/slices/swapSlice";
import { setOrderStore } from "utils/store";
import chaingeAbi from "data/chaingeAbi";


// btnStatus -1 未连接 -2 交易参数不完整 -3 余额不足 -4 交易资产相同 -5 不足5U -6 滑点过高 -7 链不同 -8 价格差异太大 -9 toAMount小于等于0 -10 disableBridge =true, 只能支持同链兑换
const useMainBtn = () => {
  const dispatch = useAppDispatch();
  const { wagmiChains, refreshData } = useAppSelector((state) => state.app);
  const { isConnected } = useAppSelector((state) => state.wallet);
  const { address, connector } = useAccount();
  const {
    fromToken,
    fromChain,
    toChain,
    toToken,
    fromAmount,
    balanceEnough,
    isMinUsdt,
    slippage,
    aggregator,
    toAddress,
    routeSummary,
    priceImpact,
    toAmount,
    minUsdt,
    thirdSwap,
    extra,
    balanceEvm
  } = useAppSelector((state) => state.coreInfo);
  const { isCrossChain, aggregateType } = useIsCrossChain();
  const [btnStatus, setBtnStatus] = useState(-1);
  const [btnText, setBtnText] = useState("Swap now");
  const [btnActive, setBtnActive] = useState(true);
  const { switchNetworkAsync } = useSwitchNetwork();
  const { chain: curChain } = useNetwork();

  const checkBtnStatus = useCallback(async () => {
    if (!isConnected) {
      setBtnStatus(-1);
      return;
    }
    if (
      !(
        fromChain &&
        toChain &&
        fromToken &&
        toToken &&
        fromAmount &&
        +fromAmount > 0
      )
    ) {
      setBtnStatus(-2);
      return;
    }

    if (
      (fromToken?.disableBridge || toToken?.disableBridge) &&
      fromChain?.nickName !== toChain?.nickName
    ) {
      setBtnStatus(-10);
      return;
    }

    if (curChain?.id !== +fromChain?.network) {
      setBtnStatus(-7);
      return;
    }
    // 余额不足
    if (!balanceEnough) {
      setBtnStatus(-3);
      return;
    }
    if (
      fromChain?.nickName === toChain?.nickName &&
      fromToken?.symbol === toToken?.symbol
    ) {
      setBtnStatus(-4);
      return;
    }
    if (isMinUsdt) {
      setBtnStatus(-5);
      return;
    }
    if (slippage >= 20 || +priceImpact >= 20) {
      setBtnStatus(-6);
      return;
    }
    if (+toAmount <= 0) {
      setBtnStatus(-9);
      return;
    }
    setBtnStatus(1);
  }, [
    isConnected,
    fromChain,
    toChain,
    fromToken,
    toToken,
    fromAmount,
    curChain?.id,
    balanceEnough,
    isMinUsdt,
    slippage,
    priceImpact,
    toAmount,
  ]);

  useEffect(() => {
    checkBtnStatus();
  }, [checkBtnStatus]);

  useEffect(() => {
    switch (btnStatus) {
      case -1:
        setBtnText("Connect your wallet");
        break;
      case -2:
        setBtnText("Input an amount to swap");
        break;
      case -3:
        setBtnText("Your balance is not enough");
        break;
      case -4:
        setBtnText("You can’t swap identical assets");
        break;
      case -5:
        setBtnText(`Swap amount must exceed ${minUsdt} USDT`);
        break;
      case -6:
      case -8:
        setBtnText("Swap now");
        break;
      case -7:
        setBtnText(`Switch chain to ${fromChain?.fullName}`);
        break;
      case -9:
        setBtnText(`Insufficient input amount`);
        break;
      case -10:
        let tokenName = "this token";
        if (fromToken?.disableBridge) {
          tokenName = fromToken?.symbol;
        } else if (toToken?.disableBridge) {
          tokenName = toToken?.symbol;
        }
        setBtnText(`Bridge disabled for ${tokenName}, try use same chains`);
        break;
      default:
        setBtnText(`Swap now`);
    }
    if ([-2, -3, -4, -5, -10].includes(btnStatus)) {
      setBtnActive(false);
    } else {
      setBtnActive(true);
    }
  }, [
    btnStatus,
    fromChain?.fullName,
    fromToken?.disableBridge,
    fromToken?.symbol,
    minUsdt,
    toToken?.disableBridge,
    toToken?.symbol,
  ]);

  // chaing 合约 appvoe
  const chaingeApprove = useCallback(async (publicClient: any, walletClient: any, curWagmiChain: Chain, chaingeOrderContractAddress: string) => {
    if (!fromToken || !fromChain) return false;
    const contractAddress = fromToken?.contractAddress.toLowerCase();
    // const chaingeOrderContractAddress = fromChain.otherInfo?.address

    let approveAmount = parseUnits(
      fromAmount,
      fromToken.decimals
    ).toString() as any;

    // let approveAllAmount = parseUnits(balanceEvm, fromToken.decimals).toString() as any
    let approveAllAmount = numberToHex(maxUint256)
    
    if (contractAddress === DEF_TOKEN_ADDRESS.toLowerCase()) {
      dispatch(setSwapStatus(1));
      return true;
    }
    const fromAddress = address;
    const result = await publicClient.readContract({
      address: contractAddress,
      abi: inscriptionABI,
      functionName: "allowance",
      args: [fromAddress, chaingeOrderContractAddress],
    });
    if (result) {
      const approveAmountBig = BigInt(approveAmount);
      if (result < approveAmountBig) {
        // approveAmount = approveAmountBig;
        // approveAmount = approveAmount.toString()
      } else {
        dispatch(setSwapStatus(1));
        return true;
      }
    }
    let gasPrice = await publicClient?.getGasPrice();
    gasPrice = gasPrice * BigInt(3) / BigInt(2);

    let gasLimit = await publicClient?.estimateContractGas({
      address: contractAddress,
      abi: inscriptionABI,
      functionName: "approve",
      account: fromAddress,
      gasPrice: gasPrice,
      args: [chaingeOrderContractAddress, approveAllAmount],
    });

    const hash = await walletClient?.writeContract({
      address: contractAddress,
      abi: inscriptionABI,
      functionName: "approve",
      account: fromAddress,
      chain: curWagmiChain,
      gasPrice: gasPrice,
      gas: gasLimit,
      args: [chaingeOrderContractAddress, approveAllAmount],
    });
    dispatch(setSwapStatus(1));
    const txInfo = await publicClient?.waitForTransactionReceipt({
      hash: hash,
    });
    if (txInfo.status.toLowerCase() !== "success") {
      toast.error("Approval failed, try again later.", {
        duration: 3000,
        position: "top-right",
      });
      return false;
    }
    return true;
  }, [address, dispatch, fromAmount, fromChain, fromToken])

  // ERC20币跨链
  const fnCoreServiceByERC20 = useCallback(
    async (orderType: string) => {
      // 对于aggregate 交易 滑点不能为0
      if (orderType === "2" && +slippage <= 0) return;
      if(!fromChain || !toChain || !fromToken || !toToken || !fromAmount || (+fromAmount <= 0)) {
        return
      }
      try {
        const slippageForamt = formatSlippageToPerTenThousand(slippage + "");
        const curWagmiChain = wagmiChains.find(
          (item) => item.id === +fromChain?.network
        );
        if (!curWagmiChain) return;
        const provider = await connector?.getProvider();
        const fromAddress = address as `0x${string}`;
        const tokenContractAddress = fromToken?.contractAddress as `0x${string}`;
        const amount = parseUnits(fromAmount, fromToken?.decimals);
        const burnable = fromToken?.burnable
        const chaingeOrderContractAddress = fromChain.otherInfo?.address as `0x${string}`

        let value = BigInt(0)
        if(tokenContractAddress.toLowerCase() === DEF_TOKEN_ADDRESS) {
          value = amount
        } else {
          value = BigInt(0)
        }

        const publicClient = createPublicClient({
          chain: curWagmiChain,
          transport: http(curWagmiChain.rpcUrls.default.http[0]),
        }) as any;

        // 合约准备
        const walletClient = createWalletClient({
          account: fromAddress,
          chain: curWagmiChain,
          transport: custom(provider),
        }) as any;

        try {
          const isApproved = await chaingeApprove(publicClient, walletClient, curWagmiChain, chaingeOrderContractAddress)
          if(!isApproved) {
            dispatch(setSwapStatus(99))
            return
          }
        } catch(error) {
          dispatch(setSwapStatus(99))
          throw error
        }

        // 准备参数
        const tempToAddr = toAddress ? toAddress : fromAddress;
        const sourceCertsObj = {
          fromAmount: amount.toString(),
          fromIndex: fromToken?.index.toString(),
          fromChain: fromChain?.nickName,
          fromAddr: fromAddress,
          certHash: '',
          fromPublicKey: "",
          signature: "123456",
        };
        const sourceCertsStr = JSON.stringify(sourceCertsObj);
        let sourceCertsHex = toHex(sourceCertsStr);
        sourceCertsHex = sourceCertsHex.substring(2) as any;
        const params: ApiSubmitOrderParams = {
          sourceCerts: sourceCertsHex,
          orderType: orderType,
          toIndex: toToken?.index.toString(),
          toChain: toChain?.nickName,
          toAddr: tempToAddr,
          slippage: slippageForamt,
          execStrategy: "",
          extra: extra,
          triggerPrice: "0",
          timeout: "0",
          channel: "chainge",
        };
        const orderBytes = toHex(JSON.stringify(params));

        try {
          let gasPrice = await publicClient?.getGasPrice();
          gasPrice = (gasPrice * BigInt(3)) / BigInt(2);

          let gasLimit = await publicClient?.estimateContractGas({
            address: chaingeOrderContractAddress,
            abi: chaingeAbi,
            functionName: "vaultOut",
            account: fromAddress,
            gasPrice: gasPrice,
            value,
            args: [tokenContractAddress, amount.toString(), burnable, orderBytes],
          });

          const hash = await walletClient?.writeContract({
            address: chaingeOrderContractAddress,
            abi: chaingeAbi,
            functionName: "vaultOut",
            account: fromAddress,
            chain: curWagmiChain,
            gasPrice: gasPrice,
            gas: gasLimit,
            value,
            args: [tokenContractAddress, amount.toString(), burnable, orderBytes],
          });

          // 缓存信息
          const originParams = {
            fromChain,
            fromToken,
            toChain,
            toToken,
            fromAmount,
            toAmount,
            hash,
            createTime: new Date().getTime(),
            submit: false,
            status: "pending",
            type: "transfer",
            submitInfo: {
              params,
              raw: '',
              headers: {},
            },
          };
          setOrderStore(originParams);
        } catch (error) {
          console.log(error)
          dispatch(setSwapStatus(100));
          throw error;
        }
        dispatch(setSwapStatus(2));
      } catch (e) {
        throw e;
      } finally {
      }
    },
    [address, chaingeApprove, connector, dispatch, extra, fromAmount, fromChain, fromToken, slippage, toAddress, toAmount, toChain, toToken, wagmiChains]
  );

  // 核心提交
  const sendTransaction = useCallback(
    async (transactionInfo: any) => {
      const curWagmiChain = wagmiChains.find(
        (item) => item.id === +fromChain?.network
      );
      if (!curWagmiChain) return;
      const provider = await connector?.getProvider();
      const fromAddress = address;
      const chaingeOrderContractAddress = fromChain.otherInfo?.swapAddress as `0x${string}`
      const publicClient = createPublicClient({
        chain: curWagmiChain,
        transport: http(curWagmiChain.rpcUrls.default.http[0]),
      }) as any;
      const walletClient = createWalletClient({
        account: fromAddress,
        chain: curWagmiChain,
        transport: custom(provider),
      }) as any;
      try {
        const result = await chaingeApprove(
          publicClient,
          walletClient,
          curWagmiChain,
          chaingeOrderContractAddress
        );
        if (!result) {
          dispatch(setSwapStatus(99));
          return;
        }
      } catch (error) {
        dispatch(setSwapStatus(99));
        throw error;
      }

      // const chaingeOrderContractAddress = fromChain.otherInfo?.address as `0x${string}`
      const amount = parseUnits(fromAmount, fromToken?.decimals);
      const slippageForamt = formatSlippageToPerTenThousand(slippage + "");
      const tokenContractAddress = fromToken?.contractAddress as `0x${string}`;
      const receiveTokenContractAddress = toToken?.contractAddress as `0x${string}`

      let value = BigInt(0)
      if(tokenContractAddress.toLowerCase() === DEF_TOKEN_ADDRESS) {
        value = amount
      } else {
        value = BigInt(0)
      }

      let minAmount = ""
      if(extra) {
        const extraVal = extra.split(';')
        extraVal.forEach(item => {
          if(item.indexOf('4_') > -1) {
            minAmount = item.split('_')[1]
          }
        });
      }

      // 准备参数
      const tempToAddr = toAddress ? toAddress : fromAddress;
      const sourceCertsObj = {
        fromAmount: amount.toString(),
        fromIndex: fromToken?.index.toString(),
        fromChain: fromChain?.nickName,
        fromAddr: fromAddress,
        certHash: '',
        fromPublicKey: "",
        signature: "123456",
      };
      const sourceCertsStr = JSON.stringify(sourceCertsObj);
      let sourceCertsHex = toHex(sourceCertsStr);
      sourceCertsHex = sourceCertsHex.substring(2) as any;
      const params: ApiSubmitOrderParams = {
        sourceCerts: sourceCertsHex,
        orderType: '2',
        toIndex: toToken?.index.toString(),
        toChain: toChain?.nickName,
        toAddr: tempToAddr as `0x${string}`,
        slippage: slippageForamt,
        execStrategy: "",
        extra: extra,
        triggerPrice: "0",
        timeout: "0",
        channel: "chainge",
      };
      const orderBytes = toHex(JSON.stringify(params));

      try {
        let gasPrice = await publicClient?.getGasPrice();
        gasPrice = (gasPrice * BigInt(3)) / BigInt(2);

        let gasLimit = await publicClient?.estimateContractGas({
          address: chaingeOrderContractAddress,
          abi: chaingeAbi,
          functionName: "swap",
          account: fromAddress,
          gasPrice: gasPrice,
          value,
          args: [tokenContractAddress, amount.toString(), transactionInfo.to, receiveTokenContractAddress, tempToAddr, minAmount, transactionInfo.data, orderBytes],
        });

        const hash = await walletClient?.writeContract({
          address: chaingeOrderContractAddress,
          abi: chaingeAbi,
          functionName: "swap",
          account: fromAddress,
          chain: curWagmiChain,
          gasPrice: gasPrice,
          gas: gasLimit,
          value,
          args: [tokenContractAddress, amount.toString(), transactionInfo.to, receiveTokenContractAddress, tempToAddr, minAmount, transactionInfo.data, orderBytes],
        });

        // 缓存信息
        const originParams = {
          fromChain,
          fromToken,
          toChain,
          toToken,
          fromAmount,
          toAmount,
          hash,
          createTime: new Date().getTime(),
          submit: false,
          status: "pending",
          type: "swap",
          submitInfo: {
            params,
            raw: '',
            headers: {},
          },
        };
        setOrderStore(originParams);
      } catch (error) {
        console.log(error)
        dispatch(setSwapStatus(100));
        throw error;
      }
      dispatch(setSwapStatus(2));
    },
    [address, chaingeApprove, connector, dispatch, extra, fromAmount, fromChain, fromToken, slippage, toAddress, toAmount, toChain, toToken, wagmiChains]
  );

  // aggregate swap
  const fnAggregateSwap = useCallback(async () => {
    try {
      if (
        fromChain &&
        fromToken &&
        toToken &&
        fromAmount &&
        aggregator &&
        address &&
        slippage
      ) {
        const bigFromAmount = parseUnits(
          fromAmount,
          fromToken?.decimals
        ).toString();
        const fromAddress = address as `0x${string}`;
        const slippageForamt = formatSlippageToPerTenThousand(slippage + "");
        // const chaingeOrderContractAddress = fromChain.otherInfo?.address as `0x${string}`
        const chaingeOrderContractAddress = fromChain.otherInfo?.swapAddress as `0x${string}`
        const params = {
          chain: fromChain?.nickName,
          aggregator: aggregator,
          fromTokenAddress: fromToken.contractAddress,
          fromDecimal: fromToken.decimals.toString(),
          fromAmount: bigFromAmount,
          toTokenAddress: toToken.contractAddress,
          toDecimal: toToken.decimals.toString(),
          sender: fromAddress,
          recipient: chaingeOrderContractAddress,
          slippage: slippageForamt,
          allowPartialFill: true,
          routeSummary: routeSummary,
        };
        const result = await getAggregateSwap(params);
        if (result && result?.code === 0) {
          const transactionInfo = result.data;
          try {
            await sendTransaction(transactionInfo);
          } catch(error) {
            console.log(error)
          }
        } else {
          dispatch(setSwapLoading(false));
          toast.error(result.msg, {
            duration: 3000,
            position: "top-right",
          });
        }
      }
    } catch (error) {
      dispatch(setSwapLoading(false));
      throw error;
    }
  }, [address, aggregator, dispatch, fromAmount, fromChain, fromToken, routeSummary, sendTransaction, slippage, toToken]);

  // aggregate

  const btnHandler = useCallback(
    async (skipCheck: string = "0") => {
      if (!(fromToken && fromChain)) return;
      if (!btnActive) return;
      if (btnStatus === -1) {
        // 链接网络
        dispatch(setWalletWinOpened(true));
        return;
      }
      if (btnStatus === -7) {
        // 切换网络
        try {
          await switchNetworkAsync?.(+fromChain?.network);
        } catch (error) {
        } finally {
          return;
        }
      }
      if ((btnStatus === -6 || btnStatus === -8) && skipCheck !== "slippage") {
        // 滑点太高
        dispatch(setOpenSlippageWin(true));
        return;
      }
      try {
        dispatch(setSwapLoading(true));
        dispatch(setSwapStatus(0));
        if (isCrossChain) {
          await fnCoreServiceByERC20("1");
        } else {
          if (aggregateType === "swap" && !thirdSwap) {
            await fnAggregateSwap();
          } else {
            await fnCoreServiceByERC20("2");
          }
        }
      } catch (error: any) {
        const errorStr = JSON.stringify(error);
        let errorMsg = "";
        if (errorStr.includes("shortMessage")) {
          errorMsg = error.shortMessage;
        } else {
          errorMsg = "Service unavailable, try again later.";
        }
        if (errorMsg) {
          toast.error(errorMsg, {
            duration: 3000,
            position: "top-right",
          });
        }
      } finally {
        dispatch(setResetInfo());
        setTimeout(() => {
          dispatch(setRefreshData(refreshData + 1));
        }, 3000);
        setTimeout(() => {
          dispatch(setRefreshData(refreshData + 1));
        }, 5000);
      }
    },
    [fromToken, fromChain, btnActive, btnStatus, dispatch, switchNetworkAsync, isCrossChain, fnCoreServiceByERC20, aggregateType, fnAggregateSwap, refreshData, thirdSwap]
  );

  return { btnText, btnStatus, btnActive, fromChain, btnHandler };
};

export default useMainBtn;
